import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
} from '@/components/ui';
import { planKeySchema } from '@/lib/plan-catalog';
import { formatNumber } from '@/lib/utils';

import type { BillingSummary } from './admin-schemas';
import { useBillingSummary } from './billing-api';
import { BarList } from './charts';
import { money, planLabel } from './labels';

/**
 * בלוק המנויים בסקירה: MRR/ARR, ספירות לפי מצב, המרת ניסיון והתפלגות התוכניות.
 * המצב הנוכחי — לא תלוי בטווח התאריכים.
 */
export function SubscriptionsOverview() {
  const { t } = useTranslation('admin');
  const q = useBillingSummary();
  const d = q.data;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader tone="accent">
          <div className="flex gap-2">
            <CreditCard className="mt-0.5 size-4 text-accent" aria-hidden />
            <div>
              <CardTitle>{t('billing.title')}</CardTitle>
              <CardDescription>{t('billing.hint')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {q.isError ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          ) : !d ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <BillingStats summary={d} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader tone="violet">
          <CardTitle>{t('billing.planDistribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          {d ? <PlanDistribution plans={d.plans} /> : <Skeleton className="h-32 w-full" />}
        </CardContent>
      </Card>
    </div>
  );
}

export function BillingStats({ summary: d }: { summary: BillingSummary }) {
  const { t } = useTranslation('admin');
  const rate = d.trialConversion.rate;
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label={t('billing.mrr')} value={money(d.mrrMinor)} strong />
      <Stat label={t('billing.arr')} value={money(d.arrMinor)} strong />
      <Stat
        label={t('billing.conversion')}
        value={rate === null ? '—' : `${Math.round(rate * 100)}%`}
        hint={
          rate === null
            ? t('billing.conversionNone')
            : t('billing.conversionValue', {
                converted: formatNumber(d.trialConversion.converted),
                ended: formatNumber(d.trialConversion.ended),
              })
        }
      />
      <Stat
        label={t('billing.counts.complimentary')}
        value={formatNumber(d.counts.complimentary)}
      />
      <Stat label={t('billing.counts.active')} value={formatNumber(d.counts.active)} />
      <Stat label={t('billing.counts.trialing')} value={formatNumber(d.counts.trialing)} />
      <Stat label={t('billing.counts.pastDue')} value={formatNumber(d.counts.pastDue)} />
      <Stat label={t('billing.counts.canceled')} value={formatNumber(d.counts.canceled)} />
    </dl>
  );
}

/** לכל תוכנית: סה"כ עסקים, ובסוגריים — בתשלום / בניסיון / חינם. */
export function PlanDistribution({ plans }: { plans: BillingSummary['plans'] }) {
  const { t } = useTranslation('admin');
  return (
    <BarList
      tone="violet"
      items={planKeySchema.options.map((k) => {
        const p = plans[k];
        return {
          key: k,
          label: planLabel(k),
          value: p.paying + p.trialing + p.complimentary,
          suffix: t('billing.planBreakdown', {
            paying: formatNumber(p.paying),
            trialing: formatNumber(p.trialing),
            complimentary: formatNumber(p.complimentary),
          }),
        };
      })}
    />
  );
}

function Stat({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 px-3 py-2">
      <dt className="text-2xs text-fg-muted">{label}</dt>
      <dd
        className={
          strong ? 'tabular text-lg font-bold text-fg' : 'tabular text-sm font-semibold text-fg'
        }
      >
        <bdi>{value}</bdi>
      </dd>
      {hint ? <dd className="text-2xs text-fg-subtle">{hint}</dd> : null}
    </div>
  );
}
